// ./src/hooks/useFleetSummary.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  DashboardService,
  FleetSummary,
  FleetSummaryParamsInput,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";

// ============================================
// Types
// ============================================

export interface UseFleetSummaryOptions {
  /** Auto-load current month on mount (and on userId change). @default false */
  autoLoad?: boolean;
}

export interface UseFleetSummaryReturn {
  // Current data
  summary: FleetSummary | null;
  loading: boolean;
  isLoaded: boolean;

  // Selected month
  selectedMonth: { year: number; month: number };
  isCurrentMonth: boolean;

  // Navigation
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToCurrentMonth: () => void;
  goToMonth: (year: number, month: number) => void;

  // Operations
  refresh: () => Promise<OpResult>;
  reset: () => void;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Helpers
// ============================================

function getCurrentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

function getPreviousMonth(year: number, month: number): { year: number; month: number } {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

function getNextMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/**
 * Compare two year-month pairs.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 */
function compareYearMonth(
  a: { year: number; month: number },
  b: { year: number; month: number }
): number {
  return a.year !== b.year ? a.year - b.year : a.month - b.month;
}

// ============================================
// Hook
// ============================================

export const useFleetSummary = (
  options: UseFleetSummaryOptions = {}
): UseFleetSummaryReturn => {
  const { autoLoad = false } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Monotonic call ID for stale-response detection
  const callIdRef = useRef(0);

  // ── State ──

  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Current summary being displayed
  const [summary, setSummary] = useState<FleetSummary | null>(null);

  // Cache: monthKey → FleetSummary (past months are immutable)
  const cacheRef = useRef(new Map<string, FleetSummary>());

  // ── Derived ──

  const currentYearMonth = useMemo(getCurrentYearMonth, []);

  const isCurrentMonth = useMemo(
    () => compareYearMonth(selectedMonth, currentYearMonth) === 0,
    [selectedMonth, currentYearMonth]
  );

  // Navigation bounds from latest summary response
  const minYear = summary?.minYear ?? selectedMonth.year;
  const minMonth = summary?.minMonth ?? selectedMonth.month;

  const canGoToPreviousMonth = useMemo(() => {
    return compareYearMonth(selectedMonth, { year: minYear, month: minMonth }) > 0;
  }, [selectedMonth, minYear, minMonth]);

  const canGoToNextMonth = useMemo(() => {
    return compareYearMonth(selectedMonth, currentYearMonth) < 0;
  }, [selectedMonth, currentYearMonth]);

  // ── Core fetch ──

  const fetchMonth = useCallback(
    async (year: number, month: number, forceRefresh = false): Promise<OpResult> => {
      const key = monthKey(year, month);
      const isCurrent = compareYearMonth({ year, month }, getCurrentYearMonth()) === 0;

      // Serve from cache if available and not the current month (or forced refresh)
      if (!forceRefresh && !isCurrent && cacheRef.current.has(key)) {
        const cached = cacheRef.current.get(key)!;
        setSummary(cached);
        setIsLoaded(true);
        setLastError(null);
        return new OpResult();
      }

      const callId = ++callIdRef.current;
      setLoading(true);
      setLastError(null);

      const params: FleetSummaryParamsInput = {
        year,
        month,
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      try {
        const result = await DashboardService.getFleetSummary(params);

        // Discard stale
        if (callId !== callIdRef.current) return result;

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = result.data[0] as FleetSummary;
          setSummary(data);
          setIsLoaded(true);
          setLastError(null);

          // Cache past months (current month is always re-fetched)
          if (!isCurrent) {
            cacheRef.current.set(key, data);
          }

          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load fleet summary"));
        setIsLoaded(true);
        return result;
      } catch (error) {
        if (callId !== callIdRef.current) return OpResult.fromException(error);

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load fleet summary"));
        setIsLoaded(true);
        return result;
      } finally {
        if (callId === callIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  // ── Navigation ──

  const goToMonth = useCallback(
    (year: number, month: number) => {
      setSelectedMonth({ year, month });
      fetchMonth(year, month);
    },
    [fetchMonth]
  );

  const goToPreviousMonth = useCallback(() => {
    const prev = getPreviousMonth(selectedMonth.year, selectedMonth.month);
    goToMonth(prev.year, prev.month);
  }, [selectedMonth, goToMonth]);

  const goToNextMonth = useCallback(() => {
    const next = getNextMonth(selectedMonth.year, selectedMonth.month);
    goToMonth(next.year, next.month);
  }, [selectedMonth, goToMonth]);

  const goToCurrentMonth = useCallback(() => {
    const current = getCurrentYearMonth();
    goToMonth(current.year, current.month);
  }, [goToMonth]);

  // ── Refresh (force re-fetch current view) ──

  const refresh = useCallback(async (): Promise<OpResult> => {
    return fetchMonth(selectedMonth.year, selectedMonth.month, true);
  }, [selectedMonth, fetchMonth]);

  // ── Reset ──

  const reset = useCallback(() => {
    callIdRef.current++;
    setSummary(null);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setSelectedMonth(getCurrentYearMonth());
    cacheRef.current.clear();
  }, []);

  const clearError = useCallback(() => setLastError(null), []);

  // ── Effects ──

  // Handle user change — reset and reload
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      // Initial mount
      if (autoLoad && userId) {
        const current = getCurrentYearMonth();
        fetchMonth(current.year, current.month);
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();
      if (autoLoad && userId) {
        const current = getCurrentYearMonth();
        fetchMonth(current.year, current.month);
      }
    }
  }, [userId, autoLoad, fetchMonth, reset]);

  // ── Return ──

  return useMemo(
    () => ({
      summary,
      loading,
      isLoaded,

      selectedMonth,
      isCurrentMonth,

      canGoToPreviousMonth,
      canGoToNextMonth,
      goToPreviousMonth,
      goToNextMonth,
      goToCurrentMonth,
      goToMonth,

      refresh,
      reset,

      lastError,
      clearError,
    }),
    [
      summary,
      loading,
      isLoaded,
      selectedMonth,
      isCurrentMonth,
      canGoToPreviousMonth,
      canGoToNextMonth,
      goToPreviousMonth,
      goToNextMonth,
      goToCurrentMonth,
      goToMonth,
      refresh,
      reset,
      lastError,
      clearError,
    ]
  );
};